import { Controller } from '@hotwired/stimulus'
import DynamicSelect, { Options } from '../../modules/dynamic_select'

export default class extends Controller {
  static targets = [
    'name',
    'valueSelect',
    'valueInput',
    'valueCheckbox',
    'error',
    'fromSender',
    'recipientsList',
    'ccRecipientsList',
    'bccRecipientsList',
    'recipient',
    'thresholdScore',
    'domainThresholdScore',
    'invitationInput',
    'fieldSelect',
    'fieldProjectSelect',
    'domainValueSelect',
    'konnectifyIntegrationAppsInput',
    'konnectifyIntegrationKonnectorsInput'
  ]

  static values = {
    nameOption: String,
    valueOption: String,
    values: Array,
    valueType: String
  }

  declare readonly errorTarget: HTMLDivElement
  declare readonly nameTarget: HTMLInputElement
  declare readonly valueSelectTarget: HTMLSelectElement
  declare readonly valueInputTarget: HTMLInputElement
  declare readonly valueCheckboxTarget: HTMLInputElement
  declare readonly thresholdScoreTarget: HTMLInputElement
  declare readonly domainThresholdScoreTarget: HTMLInputElement
  declare readonly fromSenderTarget: HTMLDivElement
  declare readonly hasFromSenderTarget: boolean
  declare readonly recipientsListTarget: HTMLDivElement
  declare readonly ccRecipientsListTarget: HTMLDivElement
  declare readonly bccRecipientsListTarget: HTMLDivElement
  declare readonly recipientTargets: HTMLDivElement[]
  declare readonly invitationInputTargets: HTMLInputElement[]
  declare readonly fieldSelectTarget: HTMLInputElement
  declare readonly fieldProjectSelectTarget: HTMLInputElement
  declare readonly domainValueSelectTarget: HTMLSelectElement
  declare readonly konnectifyIntegrationAppsInputTarget: HTMLInputElement
  declare readonly konnectifyIntegrationKonnectorsInputTargets: HTMLInputElement[]

  declare readonly hasErrorTarget: boolean
  declare readonly hasThresholdScoreTarget: boolean
  declare readonly hasDomainThresholdScoreTarget: boolean
  declare readonly hasFieldSelectTarget: boolean
  declare readonly hasFieldProjectSelectTarget: boolean
  declare readonly hasDomainValueSelectTarget: boolean
  declare readonly hasKonnectifyIntegrationAppsInputTarget: boolean
  declare readonly hasKonnectifyIntegrationKonnectorsInputTarget: boolean

  declare nameOptionValue: string
  declare valueOptionValue: string
  declare valuesValue: any[]
  declare valueTypeValue: string
  declare initialState: boolean

  resetError = this.resetErrorTarget.bind(this)

  valueSelect: DynamicSelect
  recipientTypeOptions: Options

  initialize (): void {
    this.valueSelect = new DynamicSelect(this.valueSelectTarget)

    this.initialState = true
    setTimeout(() => { this.nameTarget.dispatchEvent(new Event('change')) })
  }

  updateOptions ({ detail: { options, recipientTypeOptions } }: { detail: { options: Options, recipientTypeOptions: Options } }): void {
    if (['set_facility_field', 'set_project_field'].includes(this.nameTarget.value)) {
      options = this.valuesValue
    }

    const loadingInitialState = this.initialState
    this.triggerChangeEventOnFieldSelect()

    this.valueSelect.reset(options)
    this.toggleValueTarget()

    this.showThresholdScore()
    this.showFacilityField()
    this.showProjectField()
    this.toggleRecipients(recipientTypeOptions)
    this.toggleEmailFields(recipientTypeOptions)
    this.toggleInvitationInputs()

    if (!loadingInitialState) {
      this.valueInputTarget.value = ''
    }

    this.toggleMissingValueError()
    this.toggleKonnectifyIntegrationInputs()
  }

  updateValues ({ detail: { values, valueType } }: { detail: { values: any[], valueType: string } }): void {
    this.valuesValue = values
    this.valueTypeValue = valueType

    this.valueInputTarget.value = ''
    this.valueCheckboxTarget.checked = false
    this.valueSelect.reset(this.valuesValue)

    this.toggleValueTarget()
  }

  toggleValueTarget (): void {
    setTimeout(() => {
      const noValue = ['update_certification_status', 'set_scores', 'carry_forward', 'send_to_konnectify', 'set_score_to_project_field', 'run_ccm_scan'].includes(this.nameTarget.value)
      const showSelect = this.showSelect()
      const showDateInput = this.valueSelectTarget.value === 'custom_date'

      if (showDateInput) { this.valueInputTarget.type = 'date' }
      if (this.hasDomainValueSelectTarget) { this.toggle(this.domainValueSelectTarget, this.nameTarget.value === 'set_score_to_project_field') }
      this.toggle(this.valueSelectTarget, !noValue && showSelect)
      this.toggle(this.valueInputTarget, (!noValue && !showSelect && this.valueTypeValue !== 'boolean') || showDateInput)
      this.toggle(this.valueCheckboxTarget, !noValue && !showSelect && this.valueTypeValue === 'boolean')

      this.showWideSelect(['carry_forward', 'set_scores', 'run_ccm_scan'].includes(this.nameTarget.value))
    })
  }

  loadRecipients ({ target }: { target: HTMLSelectElement }): void {
    const event = this.buildRecipientToggleEvent(true)

    target.dispatchEvent(event)
  }

  valueTypeValueChanged (): void {
    switch (this.valueTypeValue) {
      case 'float':
        this.valueInputTarget.type = 'number'
        break
      case 'datetime':
      case 'date':
        if (['yesterday', 'today', 'tomorrow'].includes(this.valueInputTarget.value)) {
          this.valueInputTarget.value = ''
        } else {
          this.valueInputTarget.type = 'date'
        }

        if (this.valueInputTarget.value.length === 0) {
          this.valueInputTarget.value = new Date().toISOString().slice(0, 10)
        }

        break
      default:
        this.valueInputTarget.type = 'text'
    }
  }

  private triggerChangeEventOnFieldSelect (): void {
    // NOTE: we do not want to trigger change events in initial state
    if (this.initialState) {
      this.initialState = false
    } else {
      setTimeout(() => {
        if (this.nameTarget.value === 'set_facility_field') { this.fieldSelectTarget.dispatchEvent(new Event('change')) }
        if (['set_project_field', 'set_score_to_project_field'].includes(this.nameTarget.value)) {
          this.fieldProjectSelectTarget.dispatchEvent(new Event('change'))
        }
      })
    }
  }

  private toggle (input: HTMLInputElement | HTMLSelectElement, show: boolean): void {
    input.disabled = !show || input.getAttribute('data-readonly') === 'true'

    if (input === this.valueCheckboxTarget) {
      this.toggleHiddenClass(input.parentElement as HTMLInputElement, show)
    } else {
      this.toggleHiddenClass(input, show)
    }
  }

  private toggleHiddenClass (el: HTMLElement, show: boolean): void {
    el.classList.toggle('hidden', !show)
  }

  private showWideSelect (show: boolean): void {
    if (this.nameTarget.parentElement != null) {
      this.nameTarget.classList.toggle('h-full', show)
      this.nameTarget.parentElement.classList.toggle('h-full', show)
    }
  }

  private showThresholdScore (): void {
    const enable = this.nameTarget.value === 'update_certification_status'

    if (this.hasThresholdScoreTarget) {
      this.toggle(this.thresholdScoreTarget, enable)
    }
    if (this.hasDomainThresholdScoreTarget) {
      this.toggle(this.domainThresholdScoreTarget, enable)
    }
  }

  private toggleRecipients (types: Options): void {
    this.recipientTypeOptions = types
    const actions = ['send_email_notification_template', 'send_sms_notification_template', 'send_invitation']
    const enable = actions.includes(this.nameTarget.value)
    const event = this.buildRecipientToggleEvent(enable)

    this.toggleHiddenClass(this.recipientsListTarget, enable)
    this.recipientTargets.forEach(target => target.dispatchEvent(event))
  }

  private toggleEmailFields (types: Options): void {
    this.recipientTypeOptions = types
    const actions = ['send_email_notification_template']
    const enable = actions.includes(this.nameTarget.value)

    this.toggleHiddenClass(this.ccRecipientsListTarget, enable)
    this.toggleHiddenClass(this.bccRecipientsListTarget, enable)

    if (this.hasFromSenderTarget && this.fromSenderTarget !== undefined) {
      this.toggleHiddenClass(this.fromSenderTarget, enable)
    }
  }

  private toggleInvitationInputs (): void {
    const enable = this.nameTarget.value === 'send_invitation'

    this.invitationInputTargets.forEach(input => this.toggle(input, enable))
  }

  private toggleMissingValueError (): void {
    if (!this.hasErrorTarget) return

    const actionsWithRequiredSelectValue = ['send_email_notification_template', 'set_remediation_task_owner']
    if (!actionsWithRequiredSelectValue.includes(this.nameTarget.value)) {
      this.resetErrorTarget()
      return
    }

    if (this.valueSelectTarget.value !== this.valueInputTarget.value && this.valueInputTarget.value !== '') {
      this.valueSelect.element.addEventListener('change', this.resetError)
      this.valueSelectTarget.classList.add('is-invalid')
      this.toggleEmptyOption()
      this.toggleHiddenClass(this.errorTarget, true)
    }
  }

  private toggleEmptyOption (): void {
    const option = new Option('', '', true, true)
    option.disabled = true

    this.valueSelectTarget.options.add(option)
    this.valueSelectTarget.value = ''
  }

  private toggleKonnectifyIntegrationInputs (): void {
    if (!(this.hasKonnectifyIntegrationAppsInputTarget && this.hasKonnectifyIntegrationKonnectorsInputTarget)) {
      return
    }

    const enable = this.nameTarget.value === 'send_to_konnectify'

    this.toggle(this.konnectifyIntegrationAppsInputTarget, enable)
    this.konnectifyIntegrationKonnectorsInputTargets.forEach(input => {
      this.toggle(input, enable && this.konnectifyIntegrationAppsInputTarget.value === input.dataset.integrationAppId)
    })
  }

  private buildRecipientToggleEvent (enable: boolean): CustomEvent {
    return new CustomEvent('recipient:toggle', { detail: { enable, types: this.recipientTypeOptions } })
  }

  private showFacilityField (): void {
    if (!this.hasFieldSelectTarget) {
      return
    }

    const enable = this.nameTarget.value === 'set_facility_field'

    this.toggle(this.fieldSelectTarget, enable)
  }

  private showProjectField (): void {
    if (!this.hasFieldProjectSelectTarget) {
      return
    }

    const enable = ['set_project_field', 'set_score_to_project_field'].includes(this.nameTarget.value)

    this.toggle(this.fieldProjectSelectTarget, enable)

    if (enable) this.toggleProjectFieldOptions(this.nameTarget.value !== 'set_score_to_project_field')
  }

  private toggleProjectFieldOptions (showAll: boolean): void {
    this.fieldProjectSelectTarget.querySelectorAll('option').forEach(option => {
      const show = showAll || ['float', 'string', 'text'].includes(option.dataset.valueType as string)

      option.hidden = !show
      option.disabled = !show
      option.selected = option.selected && show
    })
    this.fieldProjectSelectTarget.querySelectorAll('optgroup').forEach(optgroup => {
      optgroup.hidden = Array.from(optgroup.querySelectorAll('option:not([hidden])')).length === 0
    })
  }

  private showSelect (): boolean {
    if (['send_email_notification_template', 'set_remediation_task_owner'].includes(this.nameTarget.value)) {
      return this.valueSelectTarget.options.length >= 0
    } else {
      return this.valueSelectTarget.options.length > 0
    }
  }

  private resetErrorTarget (): void {
    if (this.hasErrorTarget) {
      this.valueSelect.element.removeEventListener('change', this.resetError)
      this.valueSelectTarget.classList.remove('is-invalid')

      this.removeEmptyOptions()
      this.toggleHiddenClass(this.errorTarget, false)
    }
  }

  private removeEmptyOptions (): void {
    Array.from(this.valueSelectTarget.options).forEach(option => {
      if (option.value === '') {
        this.valueSelectTarget.options.remove(option.index)
      }
    })
  }
}
